import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "@/App";
import {
  Shield, Upload, Trash2, Edit2, Search, FileText,
  LogIn, LogOut, Plus, X, Eye, BookOpen
} from "lucide-react";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const BRANCHES = [
  { code: "AIML", name: "AI & Machine Learning" },
  { code: "BT", name: "Biotechnology" },
  { code: "CHE", name: "Chemical Engineering" },
  { code: "CV", name: "Civil Engineering" },
  { code: "CSE", name: "Computer Science & Engineering" },
  { code: "DS", name: "Data Science" },
  { code: "ECE", name: "Electronics & Communication Engineering" },
  { code: "EEE", name: "Electrical & Electronics Engineering" },
  { code: "EIE", name: "Electronics & Instrumentation Engineering" },
  { code: "IEM", name: "Industrial Engineering & Management" },
  { code: "ISE", name: "Information Science & Engineering" },
  { code: "ME", name: "Mechanical Engineering" },
];
const UNIVERSITIES = [
  { id: "vtu", name: "VTU" },
  { id: "bmsce", name: "BMSCE" },
];
const SEMESTERS = [1, 2, 3, 4, 5, 6, 7, 8];
const TYPES = ["notes", "pyq", "syllabus", "lab_manual"];

function formatBytes(b) {
  if (!b) return "0 B";
  if (b < 1024) return b + " B";
  if (b < 1048576) return (b / 1024).toFixed(1) + " KB";
  return (b / 1048576).toFixed(1) + " MB";
}

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [loggedIn, setLoggedIn] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [notes, setNotes] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [editingSubject, setEditingSubject] = useState(null);
  const [search, setSearch] = useState("");
  const [filterBranch, setFilterBranch] = useState("");
  const [filterSem, setFilterSem] = useState(0);
  const [filterUni, setFilterUni] = useState("");

  // Upload form
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [subjectName, setSubjectName] = useState("");
  const [universityId, setUniversityId] = useState("vtu");
  const [branch, setBranch] = useState("CSE");
  const [semester, setSemester] = useState(1);
  const [subjectId, setSubjectId] = useState("");
  const [resType, setResType] = useState("notes");
  const [description, setDescription] = useState("");

  // Subject form
  const [subjFormName, setSubjFormName] = useState("");
  const [subjFormCode, setSubjFormCode] = useState("");
  const [subjFormUni, setSubjFormUni] = useState("vtu");
  const [subjFormBranch, setSubjFormBranch] = useState("CSE");
  const [subjFormSem, setSubjFormSem] = useState(1);
  const [subjFormCredits, setSubjFormCredits] = useState(4);
  const [subjFormDesc, setSubjFormDesc] = useState("");

  const headers = { Authorization: `Bearer ${token}` };

  const tryLogin = async (e) => {
    e.preventDefault();
    try {
      const form = new FormData();
      form.append("email", email);
      form.append("password", password);
      const res = await axios.post(`${API}/auth/login`, form);
      if (!res.data.user.is_admin) {
        toast.error("Not an admin account");
        return;
      }
      localStorage.setItem("admin_token", res.data.token);
      setToken(res.data.token);
      setLoggedIn(true);
      toast.success("Admin logged in");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    }
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setToken("");
    setLoggedIn(false);
  };

  const checkAuth = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.is_admin) {
        setLoggedIn(true);
      }
    } catch {
      setLoggedIn(false);
      localStorage.removeItem("admin_token");
    }
  }, [token]);

  const fetchNotes = useCallback(async () => {
    if (!loggedIn) return;
    setLoading(true);
    try {
      // Fetch all notes without any filters initially to ensure resources are visible
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (filterBranch && filterBranch !== "all") params.set("branch", filterBranch);
      if (filterSem > 0) params.set("semester", String(filterSem));
      const res = await axios.get(`${API}/admin/notes?${params.toString()}`, { headers });
      console.log("Admin notes fetched:", res.data?.length || 0, "items");
      setNotes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load notes:", err);
      toast.error("Failed to load notes");
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [loggedIn, search, filterBranch, filterSem, token]);

  const fetchSubjects = useCallback(async () => {
    if (!loggedIn) return;
    try {
      const params = new URLSearchParams();
      if (filterBranch) params.set("branch", filterBranch);
      if (filterSem > 0) params.set("semester", filterSem);
      if (filterUni) params.set("university_id", filterUni);
      const res = await axios.get(`${API}/admin/subjects?${params}`, { headers });
      setSubjects(res.data);
    } catch {}
  }, [loggedIn, filterBranch, filterSem, filterUni, token]);

  const fetchStats = useCallback(async () => {
    if (!loggedIn) return;
    try {
      const res = await axios.get(`${API}/admin/stats`, { headers });
      setStats(res.data);
    } catch {}
  }, [loggedIn, token]);

  useEffect(() => { checkAuth(); }, [checkAuth]);
  useEffect(() => { fetchNotes(); fetchSubjects(); fetchStats(); }, [fetchNotes, fetchSubjects, fetchStats]);

  // Fetch subjects for dropdown when branch/semester changes in upload form
  const [availableSubjects, setAvailableSubjects] = useState([]);
  useEffect(() => {
    if (loggedIn && branch && semester > 0) {
      axios.get(`${API}/branch/${branch}/semester/${semester}/subjects`)
        .then(r => setAvailableSubjects(r.data.subjects || []))
        .catch(() => setAvailableSubjects([]));
    }
  }, [loggedIn, branch, semester]);

  const resetUploadForm = () => {
    setFile(null); setTitle(""); setSubjectName(""); setUniversityId("vtu"); setBranch("CSE");
    setSemester(1); setSubjectId(""); setResType("notes"); setDescription("");
    setEditingNote(null);
  };

  const resetSubjectForm = () => {
    setSubjFormName(""); setSubjFormCode(""); setSubjFormUni("vtu"); setSubjFormBranch("CSE");
    setSubjFormSem(1); setSubjFormCredits(4); setSubjFormDesc("");
    setEditingSubject(null);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file && !editingNote) {
      toast.error("Please select a file");
      return;
    }
    const form = new FormData();
    if (file) form.append("file", file);
    form.append("title", title);
    form.append("subject", subjectName);
    form.append("university_id", universityId);
    form.append("branch", branch);
    form.append("semester", semester);
    form.append("subject_id", subjectId);
    form.append("resource_type", resType);
    form.append("description", description);

    try {
      if (editingNote) {
        await axios.put(`${API}/admin/notes/${editingNote.id}`, form, { headers });
        toast.success("Note updated!");
      } else {
        await axios.post(`${API}/admin/upload`, form, { headers });
        toast.success("Note uploaded!");
      }
      setShowUpload(false);
      resetUploadForm();
      fetchNotes();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await axios.delete(`${API}/admin/notes/${id}`, { headers });
      toast.success("Deleted");
      fetchNotes();
      fetchStats();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleEdit = (note) => {
    setEditingNote(note);
    setTitle(note.title);
    setSubjectName(note.subject);
    setUniversityId(note.university_id || "vtu");
    setBranch(note.branch);
    setSemester(note.semester);
    setSubjectId(note.subject_id || "");
    setResType(note.resource_type);
    setDescription(note.description || "");
    setFile(null);
    setShowUpload(true);
  };

  // Subject CRUD
  const handleSubjectSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: subjFormName,
        code: subjFormCode || undefined,
        university_id: subjFormUni,
        branch: subjFormBranch,
        semester: subjFormSem,
        credits: subjFormCredits,
        description: subjFormDesc
      };
      if (editingSubject) {
        await axios.put(`${API}/admin/subjects/${editingSubject.id}`, payload, { headers });
        toast.success("Subject updated!");
      } else {
        await axios.post(`${API}/admin/subjects`, payload, { headers });
        toast.success("Subject created!");
      }
      setShowSubjectModal(false);
      resetSubjectForm();
      fetchSubjects();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save subject");
    }
  };

  const handleDeleteSubject = async (id) => {
    if (!window.confirm("Delete this subject? Resources linked to it will be unlinked.")) return;
    try {
      await axios.delete(`${API}/admin/subjects/${id}`, { headers });
      toast.success("Subject deleted");
      fetchSubjects();
      fetchStats();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleEditSubject = (subj) => {
    setEditingSubject(subj);
    setSubjFormName(subj.name);
    setSubjFormCode(subj.code || "");
    setSubjFormUni(subj.university_id || "vtu");
    setSubjFormBranch(subj.branch);
    setSubjFormSem(subj.semester);
    setSubjFormCredits(subj.credits || 4);
    setSubjFormDesc(subj.description || "");
    setShowSubjectModal(true);
  };

  // ── Login Screen ──
  if (!loggedIn) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen pt-24 pb-12 flex items-center justify-center">
        <div className="absolute top-1/3 left-1/3 w-96 h-96 bg-red-600/10 rounded-full blur-[150px] pointer-events-none" />
        <div className="relative w-full max-w-md px-4">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center rounded-2xl bg-red-600/10 border border-red-500/20 p-3 mb-4">
              <Shield className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-white mb-2">Admin Panel</h1>
            <p className="text-sm text-zinc-500">PassPoints Admin</p>
          </div>
          <form onSubmit={tryLogin} className="rounded-2xl bg-black/40 border border-white/10 backdrop-blur-xl p-6 space-y-4">
            <input
              data-testid="admin-email-input"
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Admin email" required
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-red-500/50"
            />
            <input
              data-testid="admin-password-input"
              type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Password" required
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-red-500/50"
            />
            <button
              data-testid="admin-login-btn"
              type="submit"
              className="w-full py-3 rounded-full bg-red-600 text-white font-semibold hover:bg-red-500 active:scale-[0.98] transition-transform"
            >
              <LogIn className="w-4 h-4 inline mr-2" />Sign In as Admin
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Admin Dashboard ──
  return (
    <div data-testid="admin-dashboard" className="min-h-screen pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-white">Admin Dashboard</h1>
            <p className="text-sm text-zinc-500 mt-1">Manage subjects and resources</p>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="admin-add-subject-btn"
              onClick={() => { resetSubjectForm(); setShowSubjectModal(true); }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 active:scale-95 transition-transform"
            >
              <Plus className="w-4 h-4" /> Add Subject
            </button>
            <button
              data-testid="admin-upload-btn"
              onClick={() => { resetUploadForm(); setShowUpload(true); }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-violet-600 text-white text-sm font-semibold hover:bg-violet-500 active:scale-95 transition-transform"
            >
              <Plus className="w-4 h-4" /> Upload Resource
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/5 border border-white/10 text-zinc-400 text-sm hover:text-white"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
            <div className="rounded-xl bg-white/[0.03] border border-white/10 p-4 text-center">
              <div className="font-heading text-2xl font-bold text-white">{stats.total_notes}</div>
              <div className="text-xs text-zinc-500 mt-1">Total Resources</div>
            </div>
            <div className="rounded-xl bg-white/[0.03] border border-white/10 p-4 text-center">
              <div className="font-heading text-2xl font-bold text-white">{stats.total_subjects}</div>
              <div className="text-xs text-zinc-500 mt-1">Total Subjects</div>
            </div>
            <div className="rounded-xl bg-white/[0.03] border border-white/10 p-4 text-center">
              <div className="font-heading text-2xl font-bold text-white">{BRANCHES.length}</div>
              <div className="text-xs text-zinc-500 mt-1">Branches</div>
            </div>
            <div className="rounded-xl bg-white/[0.03] border border-white/10 p-4 text-center">
              <div className="font-heading text-2xl font-bold text-white">8</div>
              <div className="text-xs text-zinc-500 mt-1">Semesters</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              data-testid="admin-search"
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="w-full bg-black/50 border border-white/10 rounded-full pl-11 pr-4 py-2.5 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-violet-500/50"
            />
          </div>
          <Select value={filterBranch || "all"} onValueChange={(v) => setFilterBranch(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[140px] bg-black/50 border-white/10 text-white rounded-full text-sm">
              <SelectValue placeholder="Branch" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">
              <SelectItem value="all" className="text-zinc-300">All Branches</SelectItem>
              {BRANCHES.map((b) => <SelectItem key={b.code} value={b.code} className="text-zinc-300">{b.code}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={String(filterSem)} onValueChange={(v) => setFilterSem(Number(v))}>
            <SelectTrigger className="w-[120px] bg-black/50 border-white/10 text-white rounded-full text-sm">
              <SelectValue placeholder="Semester" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">
              <SelectItem value="0" className="text-zinc-300">All Sems</SelectItem>
              {SEMESTERS.map((s) => <SelectItem key={s} value={String(s)} className="text-zinc-300">Sem {s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterUni || "all"} onValueChange={(v) => setFilterUni(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[140px] bg-black/50 border-white/10 text-white rounded-full text-sm">
              <SelectValue placeholder="College" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">
              <SelectItem value="all" className="text-zinc-300">All Colleges</SelectItem>
              {UNIVERSITIES.map((u) => <SelectItem key={u.id} value={u.id} className="text-zinc-300">{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="resources" className="w-full">
          <TabsList className="bg-black/40 border border-white/10 mb-6">
            <TabsTrigger value="resources" className="data-[state=active]:bg-violet-600">Resources</TabsTrigger>
            <TabsTrigger value="subjects" className="data-[state=active]:bg-violet-600">Subjects</TabsTrigger>
          </TabsList>

          {/* Resources Tab */}
          <TabsContent value="resources">
            <div className="rounded-2xl bg-black/40 border border-white/10 backdrop-blur-md overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead className="text-zinc-500">Title</TableHead>
                      <TableHead className="text-zinc-500">Subject</TableHead>
                      <TableHead className="text-zinc-500">Branch</TableHead>
                      <TableHead className="text-zinc-500">Sem</TableHead>
                      <TableHead className="text-zinc-500">Type</TableHead>
                      <TableHead className="text-zinc-500">Size</TableHead>
                      <TableHead className="text-zinc-500">Downloads</TableHead>
                      <TableHead className="text-zinc-500 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {notes.length === 0 && (
                      <TableRow><TableCell colSpan={8} className="text-center text-zinc-500 py-12">
                        {loading ? "Loading..." : "No resources uploaded yet."}
                      </TableCell></TableRow>
                    )}
                    {notes.map((note) => (
                      <TableRow key={note.id} data-testid={`admin-note-${note.id}`} className="border-white/5">
                        <TableCell className="text-white text-sm font-medium max-w-[200px] truncate">{note.title}</TableCell>
                        <TableCell className="text-zinc-400 text-sm">{note.subject}</TableCell>
                        <TableCell className="text-zinc-400 text-sm">{note.branch}</TableCell>
                        <TableCell className="text-zinc-400 text-sm">{note.semester}</TableCell>
                        <TableCell><span className="text-xs px-2 py-1 rounded-full bg-violet-600/10 text-violet-300">{note.resource_type}</span></TableCell>
                        <TableCell className="text-zinc-500 text-sm">{formatBytes(note.file_size)}</TableCell>
                        <TableCell className="text-zinc-500 text-sm">{note.downloads || 0}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <a 
                              href={`/notes/view?file=${encodeURIComponent(note.file_url)}&title=${encodeURIComponent(note.title)}`}
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="p-2 rounded-lg hover:bg-white/5 text-zinc-500 hover:text-cyan-400 transition-colors"
                              title="View PDF"
                            >
                              <Eye className="w-4 h-4" />
                            </a>
                            <button onClick={() => handleEdit(note)}
                              className="p-2 rounded-lg hover:bg-white/5 text-zinc-500 hover:text-amber-400 transition-colors">
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button data-testid={`admin-delete-${note.id}`} onClick={() => handleDelete(note.id)}
                              className="p-2 rounded-lg hover:bg-white/5 text-zinc-500 hover:text-red-400 transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* Subjects Tab */}
          <TabsContent value="subjects">
            <div className="rounded-2xl bg-black/40 border border-white/10 backdrop-blur-md overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead className="text-zinc-500">Name</TableHead>
                      <TableHead className="text-zinc-500">Code</TableHead>
                      <TableHead className="text-zinc-500">University</TableHead>
                      <TableHead className="text-zinc-500">Branch</TableHead>
                      <TableHead className="text-zinc-500">Semester</TableHead>
                      <TableHead className="text-zinc-500">Credits</TableHead>
                      <TableHead className="text-zinc-500 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subjects.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center text-zinc-500 py-12">
                        No subjects yet. Click "Add Subject" to create one.
                      </TableCell></TableRow>
                    )}
                    {subjects.map((subj) => (
                      <TableRow key={subj.id} data-testid={`admin-subject-${subj.id}`} className="border-white/5">
                        <TableCell className="text-white text-sm font-medium">{subj.name}</TableCell>
                        <TableCell className="text-zinc-400 text-sm">{subj.code}</TableCell>
                        <TableCell className="text-zinc-400 text-sm uppercase">{subj.university_id || "-"}</TableCell>
                        <TableCell className="text-zinc-400 text-sm">{subj.branch}</TableCell>
                        <TableCell className="text-zinc-400 text-sm">{subj.semester}</TableCell>
                        <TableCell className="text-zinc-400 text-sm">{subj.credits || 4}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => handleEditSubject(subj)}
                              className="p-2 rounded-lg hover:bg-white/5 text-zinc-500 hover:text-amber-400 transition-colors">
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button data-testid={`admin-delete-subject-${subj.id}`} onClick={() => handleDeleteSubject(subj.id)}
                              className="p-2 rounded-lg hover:bg-white/5 text-zinc-500 hover:text-red-400 transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Upload Modal */}
        {showUpload && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              className="w-full max-w-lg rounded-2xl bg-zinc-900 border border-white/10 p-6 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl font-bold text-white">
                  {editingNote ? "Edit Resource" : "Upload New Resource"}
                </h2>
                <button onClick={() => { setShowUpload(false); resetUploadForm(); }}
                  className="p-2 rounded-lg hover:bg-white/5 text-zinc-500"><X className="w-5 h-5" /></button>
              </div>

              <form onSubmit={handleUpload} className="space-y-4">
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Title *</label>
                  <input data-testid="upload-title" type="text" value={title} onChange={(e) => setTitle(e.target.value)} required
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white outline-none focus:border-violet-500/50" />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">University *</label>
                  <Select value={universityId} onValueChange={setUniversityId}>
                    <SelectTrigger data-testid="upload-university" className="bg-white/5 border-white/10 text-white rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10">
                      {UNIVERSITIES.map((u) => <SelectItem key={u.id} value={u.id} className="text-zinc-300">{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Branch *</label>
                    <Select value={branch} onValueChange={setBranch}>
                      <SelectTrigger data-testid="upload-branch" className="bg-white/5 border-white/10 text-white rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-white/10 max-h-60">
                        {BRANCHES.map((b) => <SelectItem key={b.code} value={b.code} className="text-zinc-300">{b.code} - {b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Semester *</label>
                    <Select value={String(semester)} onValueChange={(v) => setSemester(Number(v))}>
                      <SelectTrigger data-testid="upload-semester" className="bg-white/5 border-white/10 text-white rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-white/10">
                        {SEMESTERS.map((s) => <SelectItem key={s} value={String(s)} className="text-zinc-300">Semester {s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Link to Subject (optional)</label>
                  <Select value={subjectId || "none"} onValueChange={(v) => setSubjectId(v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="upload-subject-link" className="bg-white/5 border-white/10 text-white rounded-xl">
                      <SelectValue placeholder="Select subject..." />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10 max-h-60">
                      <SelectItem value="none" className="text-zinc-300">-- No subject linked --</SelectItem>
                      {availableSubjects.map((s) => <SelectItem key={s.id} value={s.id} className="text-zinc-300">{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Subject Name (display) *</label>
                  <input data-testid="upload-subject" type="text" value={subjectName} onChange={(e) => setSubjectName(e.target.value)} required
                    placeholder="e.g. Data Structures"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white outline-none focus:border-violet-500/50" />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Resource Type *</label>
                  <Select value={resType} onValueChange={setResType}>
                    <SelectTrigger data-testid="upload-type" className="bg-white/5 border-white/10 text-white rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10">
                      {TYPES.map((t) => <SelectItem key={t} value={t} className="text-zinc-300">{t === "pyq" ? "PYQ" : t === "lab_manual" ? "Lab Manual" : t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Description</label>
                  <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white outline-none focus:border-violet-500/50 h-20 resize-none" />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">{editingNote ? "Replace File (optional)" : "File *"}</label>
                  <div className="relative">
                    <input
                      data-testid="upload-file"
                      type="file"
                      accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp"
                      onChange={(e) => setFile(e.target.files[0])}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white file:mr-4 file:rounded-full file:border-0 file:bg-violet-600/20 file:px-4 file:py-1 file:text-sm file:text-violet-300 file:cursor-pointer"
                    />
                  </div>
                  {file && <p className="text-xs text-zinc-500 mt-1">{file.name} ({formatBytes(file.size)})</p>}
                  {editingNote && !file && <p className="text-xs text-zinc-600 mt-1">Current: {editingNote.file_name}</p>}
                </div>
                <button data-testid="upload-submit-btn" type="submit"
                  className="w-full py-3 rounded-full bg-violet-600 text-white font-semibold hover:bg-violet-500 active:scale-[0.98] transition-transform flex items-center justify-center gap-2">
                  <Upload className="w-4 h-4" />
                  {editingNote ? "Update Resource" : "Upload Resource"}
                </button>
              </form>
            </motion.div>
          </div>
        )}

        {/* Subject Modal */}
        {showSubjectModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              className="w-full max-w-md rounded-2xl bg-zinc-900 border border-white/10 p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl font-bold text-white">
                  {editingSubject ? "Edit Subject" : "Add New Subject"}
                </h2>
                <button onClick={() => { setShowSubjectModal(false); resetSubjectForm(); }}
                  className="p-2 rounded-lg hover:bg-white/5 text-zinc-500"><X className="w-5 h-5" /></button>
              </div>

              <form onSubmit={handleSubjectSubmit} className="space-y-4">
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Subject Name *</label>
                  <input data-testid="subject-name" type="text" value={subjFormName} onChange={(e) => setSubjFormName(e.target.value)} required
                    placeholder="e.g. Data Structures and Algorithms"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white outline-none focus:border-emerald-500/50" />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Subject Code (optional)</label>
                  <input data-testid="subject-code" type="text" value={subjFormCode} onChange={(e) => setSubjFormCode(e.target.value)}
                    placeholder="e.g. 21CS32"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white outline-none focus:border-emerald-500/50" />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">University *</label>
                  <Select value={subjFormUni} onValueChange={setSubjFormUni}>
                    <SelectTrigger data-testid="subject-university" className="bg-white/5 border-white/10 text-white rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10">
                      {UNIVERSITIES.map((u) => <SelectItem key={u.id} value={u.id} className="text-zinc-300">{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Branch *</label>
                    <Select value={subjFormBranch} onValueChange={setSubjFormBranch}>
                      <SelectTrigger data-testid="subject-branch" className="bg-white/5 border-white/10 text-white rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-white/10 max-h-60">
                        {BRANCHES.map((b) => <SelectItem key={b.code} value={b.code} className="text-zinc-300">{b.code}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Semester *</label>
                    <Select value={String(subjFormSem)} onValueChange={(v) => setSubjFormSem(Number(v))}>
                      <SelectTrigger data-testid="subject-semester" className="bg-white/5 border-white/10 text-white rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-white/10">
                        {SEMESTERS.map((s) => <SelectItem key={s} value={String(s)} className="text-zinc-300">Sem {s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Credits</label>
                  <input data-testid="subject-credits" type="number" min="1" max="10" value={subjFormCredits} onChange={(e) => setSubjFormCredits(Number(e.target.value))}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white outline-none focus:border-emerald-500/50" />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Description</label>
                  <textarea value={subjFormDesc} onChange={(e) => setSubjFormDesc(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white outline-none focus:border-emerald-500/50 h-16 resize-none" />
                </div>
                <button data-testid="subject-submit-btn" type="submit"
                  className="w-full py-3 rounded-full bg-emerald-600 text-white font-semibold hover:bg-emerald-500 active:scale-[0.98] transition-transform flex items-center justify-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  {editingSubject ? "Update Subject" : "Create Subject"}
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}
